import * as R from 'ramda';
import i18n from 'i18next';
import isPropValid from '@emotion/is-prop-valid';
// constants
import * as C from '../constants';
//////////////////////////////////////////////////

const notEquals = R.complement(R.equals);
const isTrue = R.equals(true);
const isNot = R.complement(R.is);
const mapIndexed = R.addIndex(R.map);
const isNotNil = R.complement(R.isNil);
const isNotEmpty = R.complement(R.isEmpty);
const notInclude = R.complement(R.includes);
const isFalse = value => R.equals(value, false);
const isAllTrue = (...arg) => R.all(isTrue, arg);
const isAnyTrue = (...arg) => R.any(isTrue, arg);
const isAllFalse = (...arg) => R.all(isFalse, arg);
const isNilOrEmpty = value => R.or(R.isNil(value), R.isEmpty(value));
const isNotNilAndNotEmpty = value => R.and(isNotNil(value), isNotEmpty(value));
const isArray = R.is(Array);
const isObject = R.is(Object);
const isString = R.is(String);
const isNumber = R.is(Number);
const isBoolean = R.is(Boolean);
const isFunction = R.is(Function);

const quickClone = obj => {
  // Try to clone object with JSON.parse
  // because for complicated objects Ramda.clone
  // can take few seconds
  let clone;

  try {
    clone = JSON.parse(JSON.stringify(obj));
  } catch (error) {
    clone = R.clone(obj);
  }

  return clone;
};

// conditions
const ifElse = (predicate, ifSt, elseSt) => {
  if (predicate) return ifSt;

  return elseSt;
};

const isAllEquals = argsArr => {
  if (R.lt(R.length(argsArr), 2)) return true;

  return R.all(R.equals(R.head(argsArr)), argsArr);
};

// strings
const isEmptyString = value => R.and(isString(value), R.isEmpty(value));

const capitalizeFirstStrCharacter = s => {
  if (isNilOrEmpty(s)) return;

  return s[0].toUpperCase() + s.slice(1);
};

const capitalizeEveryFirstStrCharacter = s => {
  if (isNilOrEmpty(s)) return '';

  if (R.or(R.not(isString(s)), R.lte(R.length(s), 2))) return s;

  return R.compose(
    R.join(' '),
    R.map(
      R.compose(
        R.join(''),
        R.over(R.lensIndex(0), R.toUpper),
      ),
    ),
    R.filter(R.complement(R.isEmpty)),
    R.split(' '),
    R.toLower,
  )(s);
};

const fromEmptyStringToNull = value => ifElse(isEmptyString(value), null, value);

const fromNullToEmptyString = value => ifElse(R.isNil(value), '', value);

const createStringFromArray = (joiner = ' ', arr = []) => {
  if (R.not(isArray(arr))) return '';

  const check = item => R.and(isNotEmpty(item), isNotNil(item));

  return arr.filter(check).join(joiner);
};

const cleanStringFromLinesAndSpaces = R.compose(
  R.trim,
  R.replace(/\s+/g, ' '),
  value => {
    if (isString(value)) return value;

    if (isNumber(value)) return R.toString(value);

    return '';
  },
  R.defaultTo(''),
);

const getTranslation = (key, defaultValue) => {
  const translation = ifElse(i18n.exists(key), i18n.t(key), null);

  return R.defaultTo(defaultValue, translation);
};

// getters
const getPropFromObject = (propName, object) => R.path([propName], object);
const getOrElse = (obj, prop, elseSt) => {
  if ((isNotNil(obj) && isNotNil(R.path([prop], obj)))) return R.path([prop], obj);

  return elseSt;
};

// mappers
const mapEmptyStringToNullObjectFields = R.mapObjIndexed(
  value => fromEmptyStringToNull(value),
);

const mapNullToEmptyStringObjectFields = R.mapObjIndexed(
  value => fromNullToEmptyString(value),
);

// event
const getEventTargetName = event => R.path(['target', C.FIELD_NAME], event);
const getEventTargetValue = event => R.path(['target', C.FIELD_VALUE], event);

// refs
const getRefCurrentValue = ref => ref?.current?.value;

const getCurrencySymbol = currency => R.pathOr('$', [currency, 'symbol'], C.CURRENCY_DETAILS);

const shouldForwardProp = (propName, target) => {
  if (typeof target === 'string') {
    return isPropValid(propName);
  }

  return true;
};

const contactHandler = (e, type, contact) => {
  let opener;

  if (R.equals(type, 'email')) {
    opener = `mailto:${contact}`;
  } else {
    opener = `tel:${contact}`;
  }

  window.open(opener);

  e.preventDefault();
};

const getPhoneNumber = (phone, phoneExtension) => {
  if (R.and(isNotNilAndNotEmpty(phone), isNotNilAndNotEmpty(phoneExtension))) return `+1${phone}${phoneExtension}`;

  if (isNotNilAndNotEmpty(phone)) return `+1${phone}`;

  return null;
};

const debounce = (callback, timeout = 100) => {
  let timerId;

  return (...args) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      callback.apply(this, args);
    }, timeout);
  };
};

const deepEquals = (obj1, obj2) => {
  let areEqual = false;

  try {
    areEqual = R.equals(JSON.stringify(obj1), JSON.stringify(obj2));
  } catch (error) {
    console.error(`Can't compare equality! Arguments are too complicated. \n${error}`);
  }

  return areEqual;
};

// calc
const toNumber = num => Number(num);
const curriedParseInt = R.curry(text => parseInt(text, 10));

export {
  isNot,
  isTrue,
  ifElse,
  isFalse,
  isArray,
  debounce,
  isNotNil,
  isObject,
  isString,
  isNumber,
  toNumber,
  notEquals,
  getOrElse,
  isAllTrue,
  isAnyTrue,
  isBoolean,
  isNotEmpty,
  notInclude,
  isAllFalse,
  mapIndexed,
  quickClone,
  isFunction,
  deepEquals,
  isAllEquals,
  isNilOrEmpty,
  isEmptyString,
  getPhoneNumber,
  contactHandler,
  getTranslation,
  curriedParseInt,
  getPropFromObject,
  shouldForwardProp,
  getCurrencySymbol,
  getRefCurrentValue,
  getEventTargetName,
  isNotNilAndNotEmpty,
  getEventTargetValue,
  fromEmptyStringToNull,
  fromNullToEmptyString,
  createStringFromArray,
  capitalizeFirstStrCharacter,
  cleanStringFromLinesAndSpaces,
  capitalizeEveryFirstStrCharacter,
  mapEmptyStringToNullObjectFields,
  mapNullToEmptyStringObjectFields,
};
